import axios from "axios";

// Strip trailing slashes — a REACT_APP_BACKEND_URL like "https://site.ca/"
// would otherwise produce "//api/..." URLs that miss the API ingress rule.
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
export const API_BASE = `${BACKEND_URL}/api`;

const TOKEN_KEY = "crc_session_token";
let sessionVersion = 0;

export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
};

export const setToken = (t) => {
  if ((t || "") !== getToken()) sessionVersion += 1;
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
};

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  config._sessionVersion = sessionVersion;
  config._sessionToken = getToken();
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.response.use(
  (r) => {
    if (r.config?._sessionVersion !== sessionVersion || r.config?._sessionToken !== getToken()) {
      return Promise.reject(new axios.CanceledError("Account changed before this request completed."));
    }
    return r;
  },
  async (err) => {
    if (axios.isCancel(err)) return Promise.reject(err);
    if (err?.config?._sessionVersion !== undefined && (err.config._sessionVersion !== sessionVersion || err.config._sessionToken !== getToken())) {
      return Promise.reject(new axios.CanceledError("Account changed before this request completed."));
    }
    // ── Auto-retry once on transient upstream failures (5xx, timeout).
    // Keeps mobile users on flaky networks from seeing a spurious
    // "network error" toast when the request would have worked on a
    // fresh TCP attempt. GET only — never retry mutations because we
    // can't tell if the write already committed.
    const cfg = err?.config || {};
    const method = (cfg.method || "get").toLowerCase();
    const status = err?.response?.status;
    const isTransient = !err?.response || status >= 500 || err?.code === "ECONNABORTED";
    if (method === "get" && isTransient && !cfg.__retried) {
      cfg.__retried = true;
      await new Promise((r) => setTimeout(r, 400));
      try { return await api.request(cfg); } catch { /* fall through */ }
    }

    // ── Fire-and-forget report to /api/client-errors so operators can
    // review broken flows in /admin/error-log. Skip 401/403/404 — those
    // are user/tenant permission signals, not developer bugs.
    if (status == null || status >= 500 || err?.code === "ECONNABORTED") {
      try {
        const bare = axios.create({ baseURL: API_BASE, timeout: 4000 });
        const t = getToken();
        bare.post("/client-errors", {
          kind: "api_client",
          message: `${(cfg.method || "GET").toUpperCase()} ${cfg.url} → ${status || err?.code || "network"}`,
          stack: String(err?.stack || "").slice(0, 3000),
          path: (typeof window !== "undefined" && window.location) ? window.location.pathname : "",
          user_agent: (typeof navigator !== "undefined" && navigator.userAgent) || "",
        }, { headers: t ? { Authorization: `Bearer ${t}` } : {} }).catch(() => {});
      } catch { /* noop */ }
    }

    // AuthProvider owns session invalidation and checks that the failed
    // request still belongs to the current token. Clearing it here would
    // allow an older /auth/me response to log out a newer login session.
    // Normalize FastAPI 422 validation errors so callers can safely render
    // err.response.data.detail directly without crashing React.
    const d = err?.response?.data?.detail;
    if (d != null && typeof d !== "string") {
      try {
        if (Array.isArray(d)) {
          err.response.data.detail = d
            .map((x) => (typeof x === "string" ? x : x?.msg || JSON.stringify(x)))
            .join(", ");
        } else if (typeof d === "object") {
          err.response.data.detail = d.msg || d.message || JSON.stringify(d);
        }
      } catch {
        err.response.data.detail = "Request failed";
      }
    }

    // ── Global raw-code sanitizer.
    // The backend still returns snake_case shorthand codes like
    // `auth_required`, `landlord_only`, `not_authenticated` for some
    // legacy endpoints. If we let those flow straight into a red toast
    // the tenant portal ends up looking like a Postman console. Rewrite
    // any obvious raw code into plain English so no page-level handler
    // has to remember to sanitise. Real human sentences (with spaces)
    // pass through untouched.
    try {
      const cur = err?.response?.data?.detail;
      if (typeof cur === "string") {
        const CODE_MAP = {
          auth_required:         "Please sign in again — your session expired.",
          not_authenticated:     "Please sign in again — your session expired.",
          session_expired:       "Please sign in again — your session expired.",
          landlord_only:         "This feature is for landlords. Sign in as a landlord to continue.",
          tenant_only:           "This feature is for tenants. Sign in as a tenant to continue.",
          admin_only:            "This is an admin-only feature.",
          invalid_credentials:   "Email or password is incorrect. Please try again.",
          email_exists:          "An account with that email already exists.",
          weak_password:         "Password is too weak. Please use at least 6 characters.",
          rate_limited:          "You're going a bit fast — please wait a moment and try again.",
          budget_exhausted:      "Smart features are catching a break — try again shortly.",
          llm_budget_exhausted:  "Smart features are catching a break — try again shortly.",
          quota_exhausted:       "Daily limit reached — please try again tomorrow.",
          lease_not_found:       "We couldn't find that lease.",
          property_not_found:    "We couldn't find that property.",
          no_active_lease:       "You don't have an active lease yet.",
        };
        const key = cur.trim().toLowerCase();
        if (CODE_MAP[key]) {
          err.response.data.detail = CODE_MAP[key];
        } else if (/^[a-z][a-z0-9_]{2,40}$/.test(key) && key.includes("_") && !key.includes(" ")) {
          err.response.data.detail = key.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()) + ".";
        }
      }
    } catch { /* never let sanitiser itself throw */ }

    // ── Global handling for HTTP 402 (smart-feature paywall).
    // We tag the error with `err.paywall = true` so pages that opt in
    // can render a proper <PaywallGate> upgrade panel (see PMWorkspace,
    // LandlordParking).
    //
    // We deliberately do NOT fire an automatic toast here — background
    // polling calls (Auto chips, portfolio widgets, side panels) can
    // 402 without the user actively navigating to a gated feature, and
    // an unsolicited "See plans" toast on the Dashboard is confusing.
    // Pages that WANT a toast can inspect `err.paywall` themselves.
    if (err?.response?.status === 402) {
      err.paywall = true;
    }

    return Promise.reject(err);
  }
);

export const formatCents = (cents, currency = "CAD") => {
  if (cents == null) return "$0";
  const value = Number(cents) / 100;
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
};

// Stripe assigns a default description of "Miscellaneous Payment" to any
// charge/payment intent that didn't receive an explicit `description=…`
// argument (usually a one-off Payment Element or a subscription off-cycle
// invoice). We never want to expose that phrasing to landlords — it looks
// like an unclassified line-item. Normalise it to just "Payment" at the
// display layer so every ledger row reads cleanly regardless of upstream.
export const humanizeTxDescription = (desc) => {
  if (!desc || typeof desc !== "string") return desc;
  const trimmed = desc.trim();
  if (/^miscellaneous\s+payment$/i.test(trimmed)) return "Payment";
  if (/^miscellaneous$/i.test(trimmed)) return "Payment";
  return desc;
};

export const formatDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("en-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return s;
  }
};

export const formatDateTime = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-CA", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return s;
  }
};

// Safely redirect to an external URL even when the app is loaded inside
// an iframe (e.g. a mobile preview pane). Strategy:
//  1. Try `window.top.location.href` to bust out of the iframe in the
//     same tab.
//  2. If that throws (cross-origin sandboxing) OR if we're not in an
//     iframe, fall back to `window.open(url, "_blank")` which opens a
//     fresh top-level browser tab and works inside webviews.
//  3. Last resort: `window.location.assign` (same-tab same-frame).
// Must be called from a user-initiated event handler so the browser
// doesn't treat `window.open` as a blocked pop-up.
export const redirectExternal = (url) => {
  if (!url) return;
  try { window.open(url, "_blank", "noopener,noreferrer"); } catch {}
  try {
    if (window.top && window.top !== window.self) {
      window.top.location.href = url;
    }
  } catch {}
  try { window.location.assign(url); } catch {}
};

// Open Stripe (or any third-party URL) from a 2-step async flow without
// losing the user-gesture context. Mobile Safari + iframe sandboxes block
// window.open() if it's called AFTER an async fetch, so we open a blank
// tab SYNCHRONOUSLY during the click, then redirect it once the URL
// arrives. If popup is blocked we return null so the caller can fall back
// to rendering a visible <a target="_blank"> link.
export const openCheckoutSync = () => {
  try {
    // NOTE: do NOT pass "noopener,noreferrer" features here — per HTML spec
    // `noopener` causes window.open() to return null, which means we lose
    // the reference and can't call `win.location.href = url` afterwards.
    // The popup is destined for Stripe (first-party safe), so the slight
    // opener-leak risk is acceptable.
    const w = window.open("about:blank", "_blank");
    if (w && !w.closed) {
      // Immediately paint a Central Rentals branded loading screen so
      // the user does NOT see a blank white tab while the /connect/*
      // fetch is in flight (that request can take 2-4 seconds because
      // Stripe's AccountLink.create is not the fastest API on earth).
      try {
        w.document.open();
        w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Redirecting to Stripe…</title><meta name="viewport" content="width=device-width,initial-scale=1"><style>
          html,body{margin:0;padding:0;height:100%;background:#0f172a;color:#f1f5f9;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}
          .wrap{min-height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:24px;}
          .logo-row{display:flex;align-items:center;gap:14px;margin-bottom:28px;}
          .logo-mark{width:56px;height:56px;background:#10b981;border-radius:14px;display:flex;align-items:center;justify-content:center;box-shadow:0 8px 24px rgba(16,185,129,.35);}
          .logo-mark svg{width:34px;height:34px;stroke:#fff;fill:none;stroke-width:2.2;stroke-linecap:round;}
          .logo-text{text-align:left;}
          .logo-title{font-family:Georgia,serif;font-weight:800;font-size:20px;letter-spacing:0.5px;color:#fff;line-height:1;}
          .logo-sub{font-family:Georgia,serif;font-size:10px;letter-spacing:6px;color:#10b981;text-transform:uppercase;margin-top:6px;line-height:1;}
          .spinner{width:52px;height:52px;border-radius:50%;border:3px solid rgba(16,185,129,.25);border-top-color:#10b981;animation:spin 0.9s linear infinite;margin-bottom:20px;}
          @keyframes spin{to{transform:rotate(360deg);}}
          .msg{font-size:14px;color:#cbd5e1;max-width:340px;line-height:1.6;}
          .brand-foot{position:absolute;bottom:24px;font-size:10px;color:#64748b;letter-spacing:2px;text-transform:uppercase;}
        </style></head><body><div class="wrap"><div class="logo-row"><div class="logo-mark"><svg viewBox="0 0 40 40"><rect x="4" y="14" width="8" height="22" rx="1"/><rect x="16" y="6" width="10" height="30" rx="1"/><rect x="30" y="18" width="6" height="18" rx="1"/></svg></div><div class="logo-text"><div class="logo-title">Central Rentals</div><div class="logo-sub">Canada</div></div></div><div class="spinner"></div><div class="msg">Securely redirecting you to Stripe — this takes just a moment.</div><div class="brand-foot">Powered by Stripe · Canadian Property OS</div></div></body></html>`);
        w.document.close();
      } catch { /* cross-origin race — very unlikely on about:blank but survivable */ }
      return w;
    }
  } catch {}
  return null;
};
export const resolveCheckout = (win, url) => {
  if (!url) return false;
  // 1. Preferred: redirect the pre-opened popup tab.
  if (win && !win.closed) {
    try { win.location.href = url; return true; } catch {}
  }
  // 2. Last resort: navigate the TOP-LEVEL frame directly. This breaks
  // out of any preview-iframe sandbox and takes the user to Stripe in
  // the same browser tab. They'll come back via Stripe's return_url.
  try {
    if (window.top && window.top !== window.self) {
      window.top.location.href = url;
      return true;
    }
  } catch {}
  // 3. Fall back to navigating the current window (works when not iframed).
  try { window.location.href = url; return true; } catch {}
  return false;
};
// FastAPI 422 returns `detail` as an array of {type, loc, msg, ...} objects —
// passing that directly to React/toast crashes with "Objects are not valid
// as a React child". Always run errors through this before displaying.
export const formatError = (err, fallback = "Something went wrong") => {
  const d = err?.response?.data?.detail ?? err?.response?.data?.message ?? err?.message;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((x) => (typeof x === "string" ? x : x?.msg || JSON.stringify(x))).join(", ") || fallback;
  }
  if (typeof d === "object") return d.msg || d.message || JSON.stringify(d);
  return String(d);
};
